/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import { Form, Formik } from "formik";
import { Box, Button, Icon, Typography, useTheme, useMediaQuery } from "@mui/material";
import TextInput from "src/components/input/TextInput";
import CustomAutocomplete from "src/components/input/Autocomplete";
import FileInput from "src/components/input/FileInput";
import {
  useObtenerDepartamentosQuery,
  useObtenerHorariosDisponibilidadQuery,
  useObtenerMunicipiosQuery,
  useObtenerPerfilesQuery,
  useObtenerSedesQuery,
} from "src/redux/api/maestrosApi";
import {
  departamentosRespToOpcionSelect,
  horariosRespToOpcionSelect,
  municipiosRespToOpcionSelect,
  perfilesRespToOpcionSelect,
  sedeRespToOpcionSelect,
} from "src/redux/api/types/maestros";
import {
  opcionesNivelEstudios,
  opcionesSexo,
  opcionesTipoIdentificacionPersonaNatural,
} from "src/utils/constantes";
import CustomDialog from "src/components/dialogo/CustomDialog";
import {
  esquemaDeValidacionNuevoUsuarioUnidad,
  setValoresInicialesUsuarioUnidad,
  IinformacionBasicaForm,
} from "./utils";
import SeleccionPerfil from "./SeleccionPerfil";
import { PerfilesId } from "src/utils/perfil";
import { PRIMARIO } from "src/styles/styles";

export interface AsociarPersonaAunidadProps {
  setInformacionBasica: (values: any) => void;
  handleNext: () => void;
  informacionBasica: IinformacionBasicaForm;
  aspiranteId: string;
}

// Component for personal information fields
const PersonalInfoSection = ({ isMobile }: { isMobile: boolean }) => (
  <>
    <Box
      display="flex"
      sx={{
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 1, md: 2 },
        mb: isMobile ? 1 : 2,
      }}
    >
      <TextInput
        label="Tipo de identificación"
        name="tipoIdentificacion"
        type="select"
        opciones={opcionesTipoIdentificacionPersonaNatural}
        data-testid="select-id"
      />
      <TextInput
        label="Número de identificación"
        name="identificacion"
        type="text"
      />
      <TextInput
        label="Primer nombre"
        name="primerNombre"
        type="text"
        toUpperCase
      />
      <TextInput
        label="Segundo nombre"
        name="segundoNombre"
        type="text"
        toUpperCase
      />
    </Box>
    
    <Box
      display="flex"
      sx={{
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 1, md: 2 },
        mb: isMobile ? 1 : 2,
      }}
    >
      <TextInput
        label="Primer apellido"
        name="primerApellido"
        type="text"
        toUpperCase
      />
      <TextInput
        label="Segundo apellido"
        name="segundoApellido"
        type="text"
        toUpperCase
      />
      <TextInput
        label="Fecha de nacimiento"
        name="fechaNacimiento"
        InputLabelProps={{ 
          shrink: true,
          sx: isMobile ? { fontSize: '16px' } : {}
        }}
        type="date"
      />
      <TextInput
        label="Sexo"
        name="sexo"
        type="select"
        opciones={opcionesSexo}
        data-testid="select-id"
      />
    </Box>
  </>
);

// Component for contact information fields
interface ContactInfoSectionProps {
  isMobile: boolean;
  departamentosResp: any[] | undefined;
  municipiosResp: any[] | undefined;
  setDepartamentoSeleccionado: (departamento: string) => void;
}

const ContactInfoSection = ({ 
  isMobile, 
  departamentosResp, 
  municipiosResp, 
  setDepartamentoSeleccionado 
}: ContactInfoSectionProps) => (
  <Box
    display="flex"
    sx={{
      flexDirection: { xs: "column", md: "row" },
      gap: { xs: 1, md: 2 },
      mb: isMobile ? 1 : 2,
    }}
  >
    <TextInput
      label="Correo principal"
      name="correoElectronico"
      type="email"
      InputLabelProps={{ 
        shrink: true,
        sx: isMobile ? { fontSize: '16px' } : {}
      }}
    />
    <TextInput 
      label="Celular" 
      name="numeroCelular" 
      type="text" 
    />
    <CustomAutocomplete
      label="Departamento"
      name="departamentoResidenciaId"
      type="autocomplete"
      setValue={setDepartamentoSeleccionado}
      opciones={
        departamentosResp
          ? departamentosRespToOpcionSelect(departamentosResp)
          : []
      }
    />
    <CustomAutocomplete
      label="Municipio"
      name="municipioResidenciaId"
      type="autocomplete"
      opciones={
        municipiosResp
          ? municipiosRespToOpcionSelect(municipiosResp)
          : []
      }
    />
  </Box>
);

// Component for profile fields
interface ProfileSectionProps {
  isMobile: boolean;
  perfilesResp: any[] | undefined;
  onChangePerfil: (valor: any) => void;
}

const ProfileSection = ({ 
  isMobile, 
  perfilesResp, 
  onChangePerfil 
}: ProfileSectionProps) => (
  <Box
    display="flex"
    sx={{
      flexDirection: { xs: "column", md: "row" },
      gap: { xs: 1, md: 2 },
      mb: isMobile ? 1 : 2,
    }}
  >
    <TextInput
      label="Perfil"
      name="perfilId"
      type="select"
      opciones={
        perfilesResp ? perfilesRespToOpcionSelect(perfilesResp) : []
      }
      data-testid="select-id"
      onChange={onChangePerfil}
    />
  </Box>
);

// Component for availability fields
interface AvailabilitySectionProps {
  isMobile: boolean;
  horariosResp: any;
  sedesResp: any;
}

const AvailabilitySection = ({ 
  isMobile, 
  horariosResp, 
  sedesResp 
}: AvailabilitySectionProps) => (
  <Box
    display="flex"
    sx={{
      flexDirection: { xs: "column", md: "row" },
      gap: { xs: 1, md: 2 },
      mb: isMobile ? 2 : 3,
    }}
  >
    <TextInput
      label="Horarios disponible"
      name="horariosDisponibles"
      type="select"
      multiple
      opciones={
        horariosResp ? horariosRespToOpcionSelect(horariosResp) : []
      }
      data-testid="select-id"
    />
    <TextInput
      label="Sedes disponibles"
      name="sedesDisponibles"
      type="select"
      multiple
      opciones={sedesResp ? sedeRespToOpcionSelect(sedesResp) : []}
    />
  </Box>
);

// Component for academic information fields
interface AcademicInfoSectionProps {
  isMobile: boolean;
  values: any;
}

const AcademicInfoSection = ({ 
  isMobile, 
  values 
}: AcademicInfoSectionProps) => (
  <>
    <Typography
      variant={isMobile ? "h6" : "h5"}
      fontWeight="bold"
      textAlign="left"
      color={PRIMARIO}
      sx={{ mb: isMobile ? 2 : 3, mt: isMobile ? 1 : 2 }}
    >
      Formación acádemica
    </Typography>
    
    <Box
      display="flex"
      sx={{
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 1, md: 2 },
        mb: isMobile ? 1 : 2,
      }}
    >
      {values.perfilId === PerfilesId.MONITOR && (
        <TextInput
          label="Nivel de estudio alcanzado"
          name="nivelFormacion"
          type="select"
          opciones={opcionesNivelEstudios}
        />
      )}
      <TextInput
        label="Nombre del programa"
        name="nombrePrograma"
        toUpperCase
        type="text"
      />
      <TextInput
        label="Institución"
        name="nombreInstitucion"
        type="text"
      />
      <TextInput
        label="Fecha de graduación"
        name="fechaGraduacion"
        type="date"
        InputLabelProps={{ 
          shrink: true,
          sx: isMobile ? { fontSize: '16px' } : {}
        }}
      />
      <TextInput
        label="Fecha expedición tarjeta profesional"
        name="fechaExpedicionTarjetaProfesional"
        type="date"
        InputLabelProps={{ 
          shrink: true,
          sx: isMobile ? { fontSize: '16px' } : {}
        }}
      />
    </Box>
  </>
);

// Component for file upload
interface FileUploadSectionProps {
  isMobile: boolean;
  values: any;
  setFieldValue: (field: string, value: any) => void;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  aspiranteId: string;
}

const FileUploadSection = ({ 
  isMobile, 
  values, 
  setFieldValue, 
  selectedFile, 
  setSelectedFile, 
  aspiranteId 
}: FileUploadSectionProps) => (
  <Box sx={{ mt: isMobile ? 1 : 2, mb: isMobile ? 2 : 3 }}>
    <FileInput
      id="soporteId"
      setValue={(value: any) => {
        setFieldValue("soporteId", value);
      }}
      value={values.soporteId}
      selectedFile={selectedFile}
      setSelectedFile={setSelectedFile}
      label="Acta de grado o diploma"
      proyectoId=""
      aspiranteId={aspiranteId}
    />
  </Box>
);

// Component for form actions
const FormActions = ({ isMobile }: { isMobile: boolean }) => (
  <Box sx={{ 
    display: "flex", 
    justifyContent: "end", 
    mt: isMobile ? 2 : 4 
  }}>
    <Button
      variant="contained"
      type="submit"
      endIcon={<Icon>chevron_right</Icon>}
      size={isMobile ? "large" : "medium"}
      sx={{
        py: isMobile ? 1.5 : 1,
        px: isMobile ? 3 : 2
      }}
    >
      Continuar
    </Button>
  </Box>
);

// Main component
const InformacionBasicaForm = ({
  handleNext,
  setInformacionBasica,
  informacionBasica,
  aspiranteId,
}: AsociarPersonaAunidadProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [valoresForm, setValoresForm] = useState(
    setValoresInicialesUsuarioUnidad(informacionBasica),
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [mostrarAdvertenciaPerfil, setMostrarAdvertenciaPerfil] = useState(false);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState<any>();

  const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState<string>("");

  // Fetch data
  const { data: departamentosResp } = useObtenerDepartamentosQuery();
  const { data: perfilesResp } = useObtenerPerfilesQuery();
  const { data: horariosResp } = useObtenerHorariosDisponibilidadQuery();
  const { data: sedesResp } = useObtenerSedesQuery();
  const { data: municipiosResp } = useObtenerMunicipiosQuery(
    departamentoSeleccionado,
    { skip: departamentoSeleccionado === "" },
  );

  const onChangePerfil = (valor: any) => {
    setMostrarAdvertenciaPerfil(true);
    setPerfilSeleccionado(valor);
  };

  const renderDialog = () => (
    <CustomDialog
      mostrar={mostrarAdvertenciaPerfil}
      onClose={() => {
        setPerfilSeleccionado(undefined);
        setMostrarAdvertenciaPerfil(false);
      }}
      contenido={<SeleccionPerfil valor={[perfilSeleccionado]} />}
      acciones={
        <Button
          onClick={() => {
            setMostrarAdvertenciaPerfil(false);
            setPerfilSeleccionado(undefined);
          }}
          size={isMobile ? "large" : "medium"}
          sx={{ py: isMobile ? 1 : 0.5 }}
        >
          Aceptar
        </Button>
      }
      titulo=""
      tamanioMaximo="md"
      fullWidth
    />
  );

  return (
    <Box sx={{ px: isMobile ? 1 : 2 }}>
      <Formik
        enableReinitialize
        initialValues={valoresForm}
        validationSchema={esquemaDeValidacionNuevoUsuarioUnidad}
        onSubmit={(values: any) => {
          handleNext();
          setInformacionBasica(values);
        }}
      >
        {({ values, setFieldValue }) => (
          <Form autoComplete="off" id="form-nuevo-usuario">
            <PersonalInfoSection isMobile={isMobile} />
            
            <ContactInfoSection 
              isMobile={isMobile} 
              departamentosResp={departamentosResp} 
              municipiosResp={municipiosResp}
              setDepartamentoSeleccionado={setDepartamentoSeleccionado}
            />
            
            <ProfileSection 
              isMobile={isMobile} 
              perfilesResp={perfilesResp}
              onChangePerfil={onChangePerfil}
            />
            
            <AvailabilitySection 
              isMobile={isMobile} 
              horariosResp={horariosResp}
              sedesResp={sedesResp}
            />
            
            <AcademicInfoSection isMobile={isMobile} values={values} />
            
            <FileUploadSection 
              isMobile={isMobile}
              values={values}
              setFieldValue={setFieldValue}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              aspiranteId={aspiranteId}
            />
            
            <FormActions isMobile={isMobile} />
          </Form>
        )}
      </Formik>

      {renderDialog()}
    </Box>
  );
};

export default InformacionBasicaForm;